import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Icon, Button, IconButton, Toolbar, InputLabel, Select, MenuItem, Snackbar } from '@mui/material';
import {
  PhotosDialog,
  PhotosDialogTitle,
  PhotosDialogContent,
  PhotosAppBar,
  AppBarTitle,
  PhotosLoader,
  PhotosDialogActions,
  PhotosDialogButton,
  LocationSelect,
  PhotosDropZone,
  ImageRoot,
  DeleteBtn,
  RankContainer,
  PosRank,
  NegRank,
} from './ReportAssessmentPhotos.styles';
import { get, size, set, map, uniqBy } from 'global/lib/lodash';
import ImageGrid from 'global/components/image-grid';
import { useScreen } from 'global/styles';
import { getBase64Image, sortAlphaNum } from 'global/utils';
import sendData from 'redux/actions/sendData';
import Confirm from 'global/components/confirm';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Alert } from 'global/components/alerts';
import useRetrieveData from 'global/hooks/useRetrieveData';
import DropArea, { revokePreviewUrls } from 'global/components/drop-area';
import FolderIcon from '@mui/icons-material/Folder';
import { useSupersets } from 'global/hooks/useSupersets';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined';
import { awaitAll } from 'global/utils/awaitAll';
import { PhotosGrid } from 'global/components/perform-grid';
import { UploadFiles } from '../../../redux/actions/imageUploader';
import { Media } from '../../../global/components/Media';
import { userHasPermissions } from 'redux/actions/userLogin';
import { Text } from 'global/components/text';

const PhotoIncludes = ['supersets', 'realm', 'images'];

const UploadImage = (props) => {
  const { file, actions } = props;

  const isGood = file.quality == 1;
  const isNeg = file.quality == 0;

  const selectPos = () => {
    actions.updateImage({ ...file, quality: 1 });
  };

  const selectNeg = () => {
    actions.updateImage({ ...file, quality: 0 });
  };

  const selectDel = (e) => {
    actions.onDeleteFile && actions.onDeleteFile(file);
  };

  const onValuesChange = (o) => {
    actions.updateImage({ ...file, ...o.values });
  };

  return (
    <ImageRoot onClick={(e) => e.stopPropagation()}>
      <Media {...file} assessment={props.assessment} scores={props.scores} realmId={file.location || props.location} opt={'tag'} onValuesChange={onValuesChange} />
      <DeleteBtn onClick={selectDel} size="small">
        <Icon>cancel</Icon>
      </DeleteBtn>
      <RankContainer>
        <PosRank onClick={selectPos}>{isGood ? <ThumbUp /> : <ThumbUpOutlined />}</PosRank>
        <NegRank onClick={selectNeg}>{isNeg ? <ThumbDown /> : <ThumbDownOutlined />}</NegRank>
      </RankContainer>
    </ImageRoot>
  );
};

function ReportAssessmentPhotos(props) {
  const { assessment, assessmentId, userCanEdit } = props;
  const include = PhotoIncludes.join(',');
  const urlParams = { filter: { assessment_id: assessmentId }, include: size(PhotoIncludes) ? include : undefined };

  const { isMobile } = useScreen();

  const [uploadModal, setUploadModal] = useState(false);
  const [folders, setFolders] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  // The upload modal unmounts its drop zone on close but keeps `uploadFiles`
  // so a reopen can still submit. The drop zone leaves those preview blob
  // URLs alive for that reason; release them when this tab goes away.
  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;
  const [location, setLocation] = useState();
  const [superset, setSuperset] = useState();
  const [alerts, setAlerts] = useState();
  const [uploadStats, setUploadStats] = useState({ isUploading: false, progress: 0 });
  const [dlStats, setDlStats] = useState();
  const [confirm, setConfirm] = useState();
  // const [wallterLoading, setWallterLoading] = useState(false);
  const dropRef = useRef(null);
  const { data, loading: _load, error, update, manuallySetData } = useRetrieveData({ url: '/realm-photos' }, { params: urlParams, build: ['realmPhotos'] });
  const photosRef = useRef(data);
  const setPhotosRef = useRef(manuallySetData);
  const assessmentRef = useRef(assessment);
  setPhotosRef.current = manuallySetData;
  assessmentRef.current = assessment;
  useEffect(() => {
    photosRef.current = data;
  }, [data]);

  useEffect(() => () => revokePreviewUrls(uploadFilesRef.current), []);

  // Score drop-zones show the media button from assessment.realmPhotoCount.
  // That field is not refreshed by /realm-photos, so copy this tab's list
  // length onto the in-memory assessment whenever the list settles.
  const patchRealmPhotoCount = (count) => {
    const setAssessmentData = get(assessmentRef.current, 'manuallySetData');
    const current = get(assessmentRef.current, 'data');
    if (!setAssessmentData || !current || Array.isArray(current) || current.id == null) return;
    if (get(current, 'realmPhotoCount', 0) === count) return;
    setAssessmentData((prev) => {
      if (!prev || Array.isArray(prev) || prev.id == null) return prev;
      if (get(prev, 'realmPhotoCount', 0) === count) return prev;
      return { ...prev, realmPhotoCount: count };
    });
  };
  const patchRealmPhotoCountRef = useRef(patchRealmPhotoCount);
  patchRealmPhotoCountRef.current = patchRealmPhotoCount;

  useEffect(() => {
    if (_load || error) return;
    patchRealmPhotoCountRef.current(size(data));
  }, [data, _load, error]);

  const updatePhotos = async (ots) => {
    const next = await update(ots);
    if (Array.isArray(next)) {
      patchRealmPhotoCountRef.current(size(next));
    }
    return next;
  };
  const { supersets: fetchedSupersets } = useSupersets({ assessment: assessment.data, scores: props.scores });
  // PFM-2547: in hashed share-link sessions the backend's per-scorecard
  // supersets endpoint may return an empty list, leaving the Photos tab
  // unable to populate its "by system" filter dropdown. Merge in any supersets
  // referenced by the photos themselves (loaded as a JSON:API include) so the
  // dropdown is generated from the data the user can actually see.
  const supersetsFromPhotos = useMemo(() => {
    const m = {};
    (data || []).forEach((p) => {
      (get(p, 'supersets') || []).forEach((s) => {
        if (s && s.id != null) m[s.id] = { ...s, id: String(s.id) };
      });
    });
    return Object.values(m);
  }, [data]);
  const supersets = useMemo(() => uniqBy([...(fetchedSupersets || []), ...supersetsFromPhotos], 'id'), [fetchedSupersets, supersetsFromPhotos]);
  const assessmentRealmId = get(assessment, 'data.realm.id');
  const canWallterSubscore = userHasPermissions(['wallterSubscore']);
  const hasSingleSuperset = supersets.length === 1;
  const canAutoTagPhotos = canWallterSubscore && size(supersets) > 1;

  const loading = _load || assessment.loading;

  const applyPhotoUpdates = (photos) => {
    const incoming = (Array.isArray(photos) ? photos : [photos]).filter((p) => p && p.id != null);
    if (!size(incoming)) {
      return;
    }

    const byId = {};
    incoming.forEach((p) => {
      byId[String(p.id)] = p;
    });

    const next = [];
    (photosRef.current || []).forEach((p) => {
      const upd = byId[String(p.id)];
      if (!upd) {
        next.push(p);
        return;
      }
      if (upd.archived) {
        return;
      }
      next.push({
        ...p,
        ...upd,
        images: size(upd.images) ? upd.images : p.images,
        realm: get(upd, 'realm.id') ? upd.realm : p.realm,
        supersets: upd.supersets !== undefined ? upd.supersets : p.supersets,
      });
    });

    photosRef.current = next;
    setPhotosRef.current(next);
  };

  useEffect(() => {
    if (data && !loading) {
      const folds = {};
      const realmPhotos = data;
      realmPhotos.forEach((rp, i) => {
        const realmid = get(rp, 'realm.id');
        const photos = get(folds, [realmid, 'photos'], []);
        set(folds, realmid, {
          id: `realm-${get(rp, 'realm.id')}`,
          name: get(rp, 'name'),
          photos: [...photos, { ...rp }].map((r) => ({ ...get(r, 'images.0', {}), ...r, assessment: assessment.data })).sort(sortAlphaNum),
          realm: get(rp, 'realm'),
          icon: FolderIcon,
        });
      });
      const fols = map(folds, (o) => o).sort((a, b) => {
        // Show the Assessment's realm_id as first folder
        if (get(a, 'realm.id') === assessmentRealmId) {
          return -1;
        } else if (get(b, 'realm.id') === assessmentRealmId) {
          return 1;
        }
        // If it's not the Assessment's realm_id, order folders alphabetically
        const A = get(a, 'realm.name');
        const B = get(b, 'realm.name');
        return A === B ? 0 : A > B ? 1 : -1;
      });
      setFolders(fols);
    }
  }, [data, loading]);

  useEffect(() => {
    setLocation(assessmentRealmId);
  }, [assessmentRealmId]);

  const uploadPhotos = () => {
    setUploadModal(true);
  };

  const confirmDownload = async () => {
    setDlStats((dl) => ({
      ...dl,
      isLoading: true,
      progress: 0,
    }));
    setConfirm();

    let totalImages = 0;
    folders.map((f) => (totalImages += f.photos.length));
    const folds = [];
    const zip = new JSZip();
    const zipFolders = {};
    await Promise.all(
      folders.map(async (f) => {
        try {
          folds.push({ ...f });
          const folderName = get(f, 'realm.name');
          set(zipFolders, folderName, zip.folder(folderName));
          if (f.photos) {
            await Promise.all(
              f.photos.map(async (_f) => {
                const data = await getBase64Image(_f.fullUrl || _f.largeUrl || _f.large, true);
                setDlStats((dl) => ({
                  ...dl,
                  progress: dl.progress + 100 / totalImages,
                }));
                folds.push({
                  ..._f,
                  folderName,
                  data,
                });
              })
            );
          }
        } catch (err) {
          setDlStats((dl) => ({
            ...dl,
            progress: dl.progress + 100 / totalImages.length,
          }));
          return null;
        }
      })
    );

    folds
      .filter((f) => f)
      .forEach((file) => {
        if (file.data) {
          get(zipFolders, file.folderName).file(file.name, file.data, { base64: true });
        }
      });

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${get(assessment, 'data.name')}-photos.zip`);
      setDlStats();
    });
  };

  const download = () => {
    setConfirm({
      title: 'Download All Images?',
      content: `Are you sure you want to download all images?`,
      onOk: () => confirmDownload(),
      onCancel: () => setConfirm(),
    });
  };

  const onDropChange = (files) => {
    setUploadFiles(files);
  };

  const onDeleteFile = (file) => {
    setUploadFiles((uf) => uf.filter((uf) => uf.id !== file.id));
  };

  const updateImage = (file) => {
    setUploadFiles((uf) =>
      uf.map((f) => {
        if (f.id === file.id) {
          f = file;
        }
        return f;
      })
    );
  };

  const onLocationChange = (loc) => {
    setLocation(loc.target.value);
  };

  const onSupersetChange = (loc) => {
    setSuperset(loc.target.value);
  };

  const uploadSuccess = () => {
    setAlerts({
      message: 'Upload success',
      severity: 'success',
    });
    clearProgress();
    // The refetch below is async and this tab unmounts on navigation, so set
    // the count now. The effect above replaces it with the fetched length.
    patchRealmPhotoCount(size(photosRef.current) + size(uploadFiles));
    updatePhotos();
    setUploadModal(false);
    setUploadFiles([]);
  };

  const uploadFail = (failed) => {
    let message = failed.map((f) => f.message || '').join(', ');
    clearProgress();
    setAlerts({
      message: (
        <>
          <p>{`${failed.length} Upload failed: ${message}`}</p>
          <p>{`These files have been placed in the upload window. Please try again.`}</p>
        </>
      ),
      severity: 'error',
    });
    setUploadFiles(failed.map((f) => f.file));
  };

  const clearProgress = () => {
    setUploadStats((upl) => ({
      ...upl,
      isUploading: false,
      progress: 0,
    }));
  };

  const cancelUpload = () => {
    setUploadStats((upl) => ({
      ...upl,
      isUploading: false,
      progress: 0,
    }));
    setUploadFiles([]);
  };

  const sendFile = async (file) => {
    try {
      await UploadFiles(file, { batchIndex: get(file, 'data.attributes.batchIndex'), batchSize: get(file, 'data.attributes.batchSize') });
      return await sendData(
        {
          url: '/realm-photos',
          method: 'post',
          data: { ...file },
        },
        { maxTries: 3 }
      );
    } catch (err) {
      console.error(err);
      throw new Error(err.message);
    }
  };

  const submitFiles = async () => {
    setUploadStats((upl) => ({
      ...upl,
      isUploading: true,
    }));

    const failed = [];

    const files = uploadFiles.map((file, index) => {
      const defaultSupersetId = hasSingleSuperset ? supersets[0].id : superset;
      let relationships;

      if (defaultSupersetId) {
        relationships = {
          supersets: {
            data: [{ type: 'supersets', id: defaultSupersetId }],
          },
        };
      } else if (!!size(file.supersets) || !!superset) {
        relationships = {
          supersets: {
            data: file.supersets ? file.supersets.map((s) => ({ type: 'supersets', id: s })) : [{ type: 'supersets', id: superset }],
          },
        };
      } else {
        relationships = undefined;
      }

      return {
        file,
        data: {
          type: 'realm-photos',
          attributes: {
            name: file.file.name,
            realm_id: file.location || location,
            assessment_id: assessmentId,
            quality: file.quality,
            images: [file],

            batchSize: size(uploadFiles),
            batchIndex: index,
          },
          relationships,
        },
      };
    });

    await awaitAll(
      files.map((f) => () => async () => {
        try {
          return await sendFile({ data: f.data });
        } catch (err) {
          failed.push({ ...f, message: err.message });
        }
      }),
      {
        queueSize: 5,
        onProgress: (p) => {
          setUploadStats((upl) => ({
            ...upl,
            progress: p * 100,
          }));
        },
      }
    );

    if (failed.length > 0) {
      uploadFail(failed);
    } else {
      uploadSuccess();
    }
  };

  const realms = uniqBy(
    [
      get(assessment, 'data.realm'),
      ...get(assessment, 'data.realm.realms', []).sort((a, b) => {
        const A = get(a, 'name');
        const B = get(b, 'name');
        return A === B ? 0 : A > B ? 1 : -1;
      }),
    ],
    'id'
  );

  const handleWallterAutoTag = async (limit) => {
    setConfirm({
      title: 'Auto-Tag',
      content: (
        <Text>
          Are you sure you want to auto-tag?
          <br />
          <Text type="label_medium">Tags will not be added to photos that have already been tagged.</Text>
        </Text>
      ),
      onOk: async () => {
        const scorecard_id = get(props, 'assessment.data.scorecards.0.id', '');
        const assessment_id = get(props, 'assessmentId', '');
        await sendData({
          url: `/wallter/realm-photos?assessment_id=${assessment_id}&scorecard_id=${scorecard_id}&limit=${limit}`,
        });
        setConfirm();
        setAlerts({ message: 'Auto-Tagging in Progress...' });
      },
      onCancel: () => setConfirm(),
    });
  };

  return (
    <>
      <PhotosGrid gutter>
        <ImageGrid
          uploadPhotos={uploadPhotos}
          download={download}
          handleWallterAutoTag={handleWallterAutoTag}
          canWallterSubscore={canAutoTagPhotos}
          dlStats={dlStats}
          folders={folders}
          includeSearch={true}
          loading={loading}
          update={updatePhotos}
          onPhotosUpdated={applyPhotoUpdates}
          userCanEdit={userCanEdit || assessment.loading}
          realms={realms}
          supersets={supersets}
          scores={props.scores}
          assessment={assessment}
        />
      </PhotosGrid>

      <PhotosDialog maxWidth={false} open={uploadModal} onClose={() => setUploadModal(false)}>
        <PhotosDialogTitle>
          <PhotosAppBar color="secondary" position="static">
            <Toolbar>
              <IconButton edge="start" onClick={() => setUploadModal(false)} aria-label="Close" size="large">
                <Icon>close</Icon>
              </IconButton>
              <AppBarTitle variant="h6">Upload Photos</AppBarTitle>
            </Toolbar>
          </PhotosAppBar>
        </PhotosDialogTitle>
        <PhotosDialogContent>
          <PhotosDropZone
            ref={dropRef}
            accept={['image/*', 'video/*']}
            // PFM-2600: previews use object URLs instead of base64 because
            // uploads go directly to S3 — base64 conversion just wastes
            // memory, especially on large batches.
            noBase64
            onFilesChange={onDropChange}
            files={uploadFiles}
            renderContent={() => <>Drop or click to upload</>}
            renderFile={(file, key) => <UploadImage key={key} file={file} location={location} assessment={assessment} scores={props.scores} actions={{ onDeleteFile, updateImage }} />}
          />
        </PhotosDialogContent>
        {uploadStats.isUploading && <PhotosLoader color="secondary" variant="determinate" value={uploadStats.progress} />}
        <PhotosDialogActions>
          {uploadStats.isUploading && (
            <PhotosDialogButton variant="contained" color="primary" onClick={cancelUpload} disabled={!uploadStats.isUploading}>
              <Icon style={{ marginRight: isMobile ? 0 : 5 }}>close</Icon>
              {isMobile ? '' : 'Cancel Upload'}
            </PhotosDialogButton>
          )}
          {!uploadStats.isUploading && (
            <LocationSelect variant="standard">
              <InputLabel shrink={true}>Category</InputLabel>
              <Select displayEmpty value={superset} onChange={onSupersetChange}>
                {[{ name: 'None', value: null }, ...supersets].map((l, i) => (
                  <MenuItem key={i} value={get(l, 'id')}>
                    {get(l, 'name')}
                  </MenuItem>
                ))}
              </Select>
            </LocationSelect>
          )}
          {!uploadStats.isUploading && (
            <LocationSelect variant="standard">
              <InputLabel>Location</InputLabel>
              <Select value={location} onChange={onLocationChange}>
                {realms
                  .filter((r) => r)
                  .map((l, i) => (
                    <MenuItem key={i} value={get(l, 'id')}>
                      {get(l, 'name')}
                    </MenuItem>
                  ))}
              </Select>
            </LocationSelect>
          )}
          {!uploadStats.isUploading && (
            <PhotosDialogButton variant="contained" color="secondary" onClick={submitFiles} disabled={!(location && size(uploadFiles) && !uploadStats.isUploading)}>
              Submit
            </PhotosDialogButton>
          )}
        </PhotosDialogActions>
      </PhotosDialog>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={alerts}
        autoHideDuration={6000}
        onClose={() => setAlerts()}
      >
        <Alert onClose={() => setAlerts()} severity={get(alerts, 'severity', 'success')}>
          {get(alerts, 'message', '')}
        </Alert>
      </Snackbar>

      <Confirm {...confirm} />
    </>
  );
}

export default ReportAssessmentPhotos;
